import { useCallback, useEffect, useRef, useState } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { DeviceWidthCombo, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { isNewUserForSidebarRestructure } from '@/utils/sidebarRolloutHelpers'
import {
  dismissSidebarNavAnnouncement,
  isSidebarNavAnnouncementDismissed,
  recordSidebarNavAnnouncementShown,
  getSidebarNavAnnouncementTemporaryCloseCount,
  recordSidebarNavAnnouncementTemporarilyClosed,
} from './sidebarNavAnnouncementStorage'

// The desktop announcement is a three step coachmark anchored to nav labels.
// None of that survives the move to a small viewport: the sidebar collapses to
// a 68px icon rail with no labels to point at, so there is nothing to anchor a
// popover to and nothing on screen for "sits right at the top" to refer to.
// Mobile therefore gets a single in-page card that names the moved items
// itself, in one pass, and reads correctly whether or not the nav is expanded.
export const MOBILE_NAV_ANNOUNCEMENT_COPY = {
  eyebrow: "What's new",
  title: 'A simpler menu, easier to navigate',
  body: 'Transactions and Documents are now at the top of the menu. Profit & Loss sits with your other reports, and the Allocation Guides have moved into a new Tools section.',
  actionLabel: 'Got it',
}

interface MobileNavAnnouncement {
  open: boolean
  eyebrow: string
  title: string
  body: string
  actionLabel: string
  /** Acknowledged for good — persists, so it does not come back on reload. */
  onAction: () => void
  /** Closed for now — not persisted, so it returns on the next visit. */
  onTemporaryClose: () => void
}

// Mirrors the desktop coachmark's events so the two surfaces are comparable.
// No `advanced` and no `dismissed`: the card is a single step whose only action
// finishes it, so `step` and `total_steps` are both 1.
const ANNOUNCEMENT_STEP = 1
const ANNOUNCEMENT_TOTAL_STEPS = 1

export const useMobileNavAnnouncement = (): MobileNavAnnouncement => {
  const track = useAnalyticsTrack()
  const currentUser = useReselector(getCurrentUser)
  const isSmallDevice = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
  // Kill switch for the announcement alone: the redesign stays on when this is off.
  const sidebarNavAnnouncementEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.sidebarNavAnnouncement,
    true
  )
  const persistentlyDismissed = isSidebarNavAnnouncementDismissed()
  const [sessionDismissed, setSessionDismissed] = useState(false)
  const [isTemporarilyClosed, setIsTemporarilyClosed] = useState(false)

  // Same eligibility gate as desktop: only users who predate the restructure
  // need it explained. Users created on or after the rollout date get Haven's
  // greeting instead and never see this.
  const isExistingUser = Boolean(
    currentUser && !isNewUserForSidebarRestructure(currentUser.createdAt)
  )

  const open =
    isSmallDevice &&
    sidebarNavAnnouncementEnabled &&
    isExistingUser &&
    !persistentlyDismissed &&
    !sessionDismissed &&
    !isTemporarilyClosed

  // Stamped onto every event for this exposure. Refs, not state: nothing
  // renders them, and handlers need the value without waiting on a re-render.
  const showCountRef = useRef(0)
  const dismissCountRef = useRef(0)
  const hasTrackedViewRef = useRef(false)

  const trackAnnouncement = useCallback(
    (event: string) =>
      track(event, {
        user_id: currentUser?.id ?? null,
        step: ANNOUNCEMENT_STEP,
        show_count: showCountRef.current,
        dismiss_count: dismissCountRef.current,
        total_steps: ANNOUNCEMENT_TOTAL_STEPS,
        surface: 'mobile_card',
      }),
    [currentUser?.id, track]
  )

  // The card renders on exactly this flag, so `open` turning true is the
  // exposure. Desktop needs the sidebar to report its own. Ref-guarded so
  // closing and reopening within one page load stays a single view.
  useEffect(() => {
    if (!open || hasTrackedViewRef.current) return
    hasTrackedViewRef.current = true
    showCountRef.current = recordSidebarNavAnnouncementShown()
    dismissCountRef.current = getSidebarNavAnnouncementTemporaryCloseCount()
    trackAnnouncement('viewed restructured sidebar nav announcement')
  }, [open, trackAnnouncement])

  const handleAction = () => {
    // The card's one action is its last, so this is completion, not dismissal.
    trackAnnouncement('completed restructured sidebar nav announcement')
    dismissSidebarNavAnnouncement()
    // Kept alongside the persisted write so the card still closes when storage
    // is unavailable (private mode), where the write above is a no-op.
    setSessionDismissed(true)
  }

  const handleTemporaryClose = () => {
    dismissCountRef.current = recordSidebarNavAnnouncementTemporarilyClosed()
    trackAnnouncement(
      'temporarily closed restructured sidebar nav announcement'
    )
    setIsTemporarilyClosed(true)
  }

  return {
    open,
    ...MOBILE_NAV_ANNOUNCEMENT_COPY,
    onAction: handleAction,
    onTemporaryClose: handleTemporaryClose,
  }
}
